// @flow
import './TreeView.css';

import * as React from 'react';
import classNames from 'classnames';
import KeyboardShortcuts from '../keyboard-shortcuts/KeyboardShortcuts';
import KeydownConfig from '../keyboard-shortcuts/KeydownConfig';
import { KeyCode } from '../util/Constants';
import ExpandButton from './ExpandButton';
import TextWithEllipsis, { TextWithEllipsisFileExtension } from './TextWithEllipsis';
import Store from '../store/Store';
import { ALL_FILES_OPTION, ASSET_LIST_BLOCK_INSTANCE, DRAG_ITEMS } from '../store/Constants';
import Spinner from './Spinner';
import { RepoItem } from '../autogenerated/ZoomBridge';
import FileInfoUtil from '../util/FileInfoUtil';
import AssetListCache, { foldersOnly } from '../queries/asset/AssetListCache';

import _ from 'lodash';
import { AssetStatusTreeView } from './AssetPropertyView';
import RemoteFileStatusHandler from '../queries/status/RemoteFileStatusHandler';
import System from '../basic/System';
import { ThemeSetup } from '../themes/ThemeSetup';
import { isMobile } from '../util/ScreenUtil';
import { toast } from '../store/StoreUtil';
import t from '../i18n/I18n';
import { isNonAliveAsset } from '../util/SelectedItemUtil';
import ViewUtil from '../ViewUtil';
import MorePopover from './context-menu/MorePopover';
import { SetterStoreUtil } from '../store/setterStoreUtil';

const debug = require('../debug/debug')('wc:common:TreeView');

const TREEVIEW = 'TREEVIEW';

export class AssetTreeNode {
  _parent: ?AssetTreeNode;
  _children: AssetTreeNode[];
  _fetchChildren: ?Function;
  _loading: ?boolean;
  _data: RepoItem;
  _hide: boolean;
  _isChildLoaded: boolean;

  constructor(data: RepoItem, parent: ?AssetTreeNode) {
    this._data = data;
    this._parent = parent;
    if (!this._data.getRemotePath()) this._data.setRemotePath(AssetTreeNode.getRemotePath(this));
    this._hide = false;
  }

  setHide(hide: boolean): void {
    this._hide = hide;
  }

  isHide(): boolean {
    return this._hide;
  }

  getFuid(): number {
    return this._data.getFuid();
  }

  getRepoItem(): RepoItem {
    return this._data;
  }

  getParent(): ?AssetTreeNode {
    return this._parent;
  }

  getChildren(): AssetTreeNode[] {
    return this._children ? this._children : [];
  }

  getChildrenSize(): number {
    return _.size(this._children);
  }

  isChildLoaded(): boolean {
    return this._isChildLoaded;
  }

  hasChildren(): boolean {
    return _.size(this._children) > 0;
  }

  hasNoChild(): boolean {
    return _.size(this._children) === 0;
  }

  getChildAt(index: number): ?AssetTreeNode {
    if (_.size(this._children) > index) {
      return _.nth(this._children, index);
    }
    return null;
  }

  setChildren(children: AssetTreeNode[]): void {
    const childrenSorted = children.sort(function (a: AssetTreeNode, b: AssetTreeNode) {
      const valueA = (a.getRepoItem().getName() || '').toLowerCase();
      const valueB = (b.getRepoItem().getName() || '').toLowerCase();
      if (valueA < valueB) {
        return -1;
      }
      if (valueA > valueB) {
        return 1;
      }
      return 0;
    });
    this._children = childrenSorted;
  }

  setChildrenFromRepoItem(repoItems: RepoItem[]): void {
    const kids: AssetTreeNode[] = repoItems.map((r: RepoItem) => {
      return new AssetTreeNode(r, this);
    });
    this._isChildLoaded = true;
    this.setChildren(kids);
  }

  async reloadCurrentLoad() {
    if (this.isFolder()) {
      this._loading = true;
      try {
        let fuids: number[] = undefined;
        fuids = AssetListCache.getInstance().getFolderKidsFuid(this.getFuid());
        const repoItems: RepoItem[] = await AssetListCache.getInstance().getOrFetchRepoItems(this.getFuid(), fuids);

        const kids = this.getChildren();
        if (kids && kids.length) {
          const newKids = [];
          for (let i = 0; i < repoItems.length; ++i) {
            const repoItem = repoItems[i];
            const item = kids.find((x) => x.getFuid() === repoItem._fuid);
            if (item) {
              newKids.push(item);
            } else {
              newKids.push(new AssetTreeNode(repoItem, this));
            }
          }
          this._isChildLoaded = true;
          this.setChildren(newKids);
        } else {
          this.setChildrenFromRepoItem(repoItems);
        }
        this._loading = false;
      } catch (err) {
        debug('[componentDidMount] error: ', err);
        this._loading = false;
      }
    }
  }

  async remoteFetch(manualRefresh = false): Promise<AssetTreeNode> {
    if (this.isFolder()) {
      this._loading = true;
      try {
        let fuids: number[] = undefined;

        if (manualRefresh) {
          fuids = AssetListCache.getInstance().getFolderKidsFuid(this.getFuid());
        } else {
          fuids = await AssetListCache.getInstance().fetchFuids(this.getFuid(), {
            fileFilters: foldersOnly,
            force: true,
            nocache: true
          });
        }

        const repoItems: RepoItem[] = await AssetListCache.getInstance().getOrFetchRepoItems(this.getFuid(), fuids);
        RemoteFileStatusHandler.getInstance()
          .submitFiles(repoItems, 0)
          .then();

        // if (System.isVAB() && manualRefresh) {
        //    RemoteFileStatusHandler.getInstance().scheduleFiles();
        // }
        this.setChildrenFromRepoItem(repoItems);
        this._loading = false;
      } catch (err) {
        debug('[componentDidMount] error: ', err);
        this._loading = false;
      }
    }
    return this;
  }

  isLoading(): boolean {
    return this._loading;
  }

  getRemotePath(): string {
    return AssetTreeNode.getRemotePath(this);
  }

  isFolder(): boolean {
    return FileInfoUtil.isFolder(this._data);
  }

  //TODO :: This is completely wrong method for calculating remote path -- Why it is done like this??.....Pawan
  static getRemotePath(node: AssetTreeNode): string {
    if (node.getRepoItem() && node.getRepoItem().getRemotePath()) return node.getRepoItem().getRemotePath();
    const paths: String[] = [];
    let current: ?AssetTreeNode = node;
    while (current) {
      if (current.getFuid() > 0) {
        const asset: RepoItem = current.getRepoItem();
        if (asset && asset.getName()) {
          paths.push(asset.getName());
        }
      }
      current = current.getParent();
    }
    return '/' + paths.reverse().join('/');
  }
}

export type TreeRootNode = {
  ...AssetTreeNode,
  __hide: boolean
};

type Props = {
  namespace: string,
  root: AssetTreeNode,
  onSelect: Function,
  getIcon: Function,
  getLabel: Function,
  getIsLeaf: Function,
  decorate?: Function,
  isIndependentTreeIns: Boolean,
  showTreeView: Boolean,
};

type State = {
  activeNode: ?AssetTreeNode,
  expandedNodes: Object
};

export default class TreeView extends React.Component<Props, State> {
  TreeViewNode: React.Component<Props, State>;
  activeContext: RepoItem;
  activeElement: any;
  treeNodeRef: HTMLElement;
  _assetListBlockRef: any = { onDeleteSuccess: _.noop, onRenameSuccess: _.noop, downloadSingleAsset: _.noop };
  _haveAssetListRef: boolean = false;
  constructor(props: Props) {
    super(props);
    this.TreeViewNode = props.decorate ? props.decorate(TreeViewNode) : TreeViewNode;
    this.state = Store.get(`TREEVIEW.${this.props.namespace}`) || {
      activeNode: new AssetTreeNode(new RepoItem().setFuid(0).setName(t('ASSETS')), null),
      expandedNodes: { [this.props.root.getFuid()]: true },
      showContextMenu: false,
      count: 0
    };
    this.state.showContextMenu = false;
    if (props.isIndependentTreeIns) {
      const activeFuid = _.last(Object.keys(props.currentExpends));
      const expNodes = {};
      this.props.currentExpends.forEach((e) => {
        expNodes[e] = true;
      });
      this.state.expandedNodes = expNodes;
      this.state.activeNode = new AssetTreeNode(new RepoItem().setFuid(activeFuid), null);
    }
  }

  componentDidMount() {
    this.addListeners();
    this.init();
    window.gs.onParentSelection = this.onParentSelection;
  }

  componentWillReceiveProps() {
    const assetsListBlockRef = Store.get(ASSET_LIST_BLOCK_INSTANCE);
    if (!this._haveAssetListRef && assetsListBlockRef) {
      this._assetListBlockRef = Store.get(ASSET_LIST_BLOCK_INSTANCE);
      this._haveAssetListRef = true;
    }
  }

  componentWillUnmount() {
    this.removeListeners();
    Store.put(`TREEVIEW.${this.props.namespace}`, { ...this.state });
    window.gs.onParentSelection = null;
  }

  triggerUpdate = () => {
    const { count } = this.state;
    this.setState({ count: count + 1 });
  };

  onRef = (node) => {
    this.treeNodeRef = node;
  };

  onContextMenuClick = (e, repo) => {
    e.preventDefault();
    if (this.props.isIndependentTreeIns) return;
    if (this.state.activeNode.getFuid() !== repo.getFuid()) this.onClick(e, repo, false);
    if (repo.getFuid() != 0) {
      this.activeContext = repo.getRepoItem();
      const x = e.clientX;
      const y = e.clientY;
      this.setState({
        showContextMenu: {
          screenX: x,
          screenY: y
        }
      });
      if (this.treeNodeRef) {
        this.activeElement = this.treeNodeRef.querySelector('[fuid="' + repo.getFuid() + '"]');
        this.activeElement.style = 'background:var(--primary-elements-bg-hover);';
      }
    }
  };

  onParentSelection = () => {
    const { activeNode } = this.state;
    this.onClick(null, activeNode.getParent(), false);
  };

  closeContextMenu = () => {
    if (this.activeElement) {
      this.activeElement.style = '';
    }
    this.setState({ showContextMenu: false });
  };

  init = () => {
    const { root } = this.props;
    let expandedNodes = { ...this.state.expandedNodes };

    // Check all expandedNodes to make sure their children have been fetched. Also
    // collapse the ones that don't have children.
    const visit = async (node: AssetTreeNode) => {
      if (!expandedNodes[node.getFuid()]) {
        return;
      }

      if (node.isFolder() && !node.isChildLoaded()) {
        node.remoteFetch();
        if (node.hasChildren()) {
          node.getChildren().forEach(visit);
        } else {
          debug('[init()] [info] node is expanded but has no children. collapsing', node);
          delete expandedNodes[node.getFuid()];
        }
      } 

      /* if (!node.fetchChildren) {
        debug('[init()] [warn] node is expanded but does not have a fetchChildren function', node);
        delete expandedNodes[node.id];
        return;
      }

      await node.fetchChildren();

      if (node.children && node.children.length) {
        node.children.forEach(visit);
      } else {
        debug('[init()] [info] node is expanded but has no children. collapsing', node);
        delete expandedNodes[node.id];
      } */
    };
    if (root.hasChildren()) {
      root.getChildren().forEach(visit);
    }

    this.setState({ expandedNodes: _.clone(expandedNodes) });
  };

  addListeners = () => {
    KeyboardShortcuts.add(
      new KeydownConfig(TREEVIEW, this.onKeyDown, [
        KeyCode.UP,
        KeyCode.DOWN,
        KeyCode.LEFT,
        KeyCode.RIGHT,
        KeyCode.ENTER
      ])
    );
  };

  removeListeners = () => {
    KeyboardShortcuts.remove(TREEVIEW);
  };

  updateActiveNodeAndExpandedNodes = (activeNode: AssetTreeNode, expandedNodeIds: number[]) => {
    let expandedNodes = { ...this.state.expandedNodes };
    expandedNodeIds.forEach((id) => (expandedNodes[id] = true));
    this.setState({ activeNode, expandedNodes: _.clone(expandedNodes) });
    this.scrollToPosition(activeNode.getFuid());
  };

  onUpArrowKeyDown = () => {
    const { activeNode } = this.state;
    if (!activeNode) {
      return;
    }

    const parent = activeNode.getParent();
    if (parent) {
      const index = parent.getChildren().indexOf(activeNode);
      if (index === -1) {
        return;
      }

      if (index > 0) {
        // Select previous sibling
        this.putActiveNodeIntoScrollView(parent.getChildAt(index - 1));
        this.setState({ activeNode: parent.getChildAt(index - 1) });
        return;
      } else {
        // Select parent
        this.putActiveNodeIntoScrollView(parent);
        this.setState({ activeNode: parent });
        return;
      }
    }
  };

  putActiveNodeIntoScrollView = (activeNode) => {
    const activeNode_Dom = document.querySelector(`[drop-fuid="${activeNode?.getFuid()}"]`);
    return activeNode_Dom ? activeNode_Dom.scrollIntoView(false) : null;
  }

  onDownArrowKeyDown = () => {
    const { activeNode, expandedNodes } = this.state;
    if (!activeNode) {
      return;
    }

    // Try to select first child
    if (expandedNodes[activeNode.getFuid()] && activeNode.hasChildren()) {
      this.putActiveNodeIntoScrollView(activeNode.getChildAt(0));
      this.setState({ activeNode: activeNode.getChildAt(0) });
      return;
    }

    // Try to select next sibling
    let parent = activeNode.getParent();
    if (parent) {
      const children = parent.getChildren();
      const index = children.indexOf(activeNode);
      this.putActiveNodeIntoScrollView(children[index + 1])
      if (index > -1 && index < children.length - 1) {
        this.setState({ activeNode: children[index + 1] });
        return;
      }
    }

    // Try to select next parent
    let grandparent = parent ? parent.getParent() : null;
    while (grandparent) {
      if (grandparent.hasNoChild()) {
        break;
      }
      const uncles = grandparent.getChildren();
      const index = uncles.indexOf(parent);
      if (index === -1) {
        break;
      }

      if (index === uncles.length - 1) {
        parent = grandparent;
        grandparent = grandparent.getParent();
        continue;
      }

      this.putActiveNodeIntoScrollView(uncles[index + 1]);
      this.setState({ activeNode: uncles[index + 1] });
      return;
    }
  };

  onLeftArrowKeyDown = () => {
    let expandedNodes = { ...this.state.expandedNodes };
    const { activeNode } = this.state;
    const parent: ?AssetTreeNode = activeNode ? activeNode.getParent() : null;
    if (activeNode && expandedNodes[activeNode.getFuid()]) {
      delete expandedNodes[activeNode.getFuid()];
      this.setState({ expandedNodes: _.clone(expandedNodes) });
      return;
    } else if (parent && parent.isHide()) {
      this.setState({ activeNode: parent });
    }
  };

  onRightArrowKeyDown = async () => {
    let expandedNodes = { ...this.state.expandedNodes };
    const { getIsLeaf } = this.props;
    const { activeNode } = this.state;

    if (activeNode) {
      const id = activeNode.getFuid();
      if (!expandedNodes[id] && !getIsLeaf(activeNode)) {
        expandedNodes[id] = true;
        await activeNode.remoteFetch();
        if (activeNode.hasChildren()) {
          activeNode.getChildren().forEach((n) => (expandedNodes[n.getFuid()] = false));
        }
        this.setState({ expandedNodes: _.clone(expandedNodes) });
      }
    }
  };

  onEnterKeyDown = () => {
    this.props.onSelect(this.state.activeNode);
  };

  onKeyDown = (e: KeyboardEvent) => {
    e.stopPropagation();
    e.preventDefault();
    switch (e.keyCode) {
      case KeyCode.UP:
        return this.onUpArrowKeyDown();
      case KeyCode.DOWN:
        return this.onDownArrowKeyDown();
      case KeyCode.LEFT:
        return this.onLeftArrowKeyDown();
      case KeyCode.RIGHT:
        return this.onRightArrowKeyDown();
      case KeyCode.ENTER:
        return this.onEnterKeyDown();
    }
  };

  scrollToPosition(fuid) {
    const treeElement = document.getElementsByName('TreeViewNode')[0];
    if (treeElement) {
      const element = treeElement.querySelector(`[fuid="${fuid}"]`);
      if (element) {
        if (this.props.isIndependentTreeIns)
          element.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
        else element.scrollIntoView();
      }
    }
  }

  onClick = async (e: MouseEvent, node: AssetTreeNode, expandOnly: boolean) => {
    e ? e.stopPropagation() : null;
    const { getIsLeaf, onSelect } = this.props;
    const expandedNodes = { ...this.state.expandedNodes };
    const id = node.getFuid();
    SetterStoreUtil.setAllFilesOption(false);

    if (!getIsLeaf(node)) {
      if (expandedNodes[id]) {
        if (expandOnly) {
          debug('[onClick()] collapsing folder');
          delete expandedNodes[id];
        }
      } else {
        debug('[onClick()] expanding folder');
        if (node.isFolder()) {
          this.triggerUpdate();
          await node.remoteFetch();
          expandedNodes[id] = true;
          node.getChildren().forEach((n) => (expandedNodes[n.getFuid()] = false));
          debug('[onClick()] Fetch Children: node: %d Children Count: %d', node.getFuid(), node.getChildrenSize());
        }
      }

      if (expandOnly) {
        debug('[onClick()] expandOnly=true', { expandedNodes });
        this.setState({ expandedNodes: _.clone(expandedNodes) });
      } else {
        debug('[onClick()] expandOnly=false', { expandedNodes, activeNode: node });
        this.setState({ expandedNodes: _.clone(expandedNodes), activeNode: node });
        onSelect(node);
      }
    } else if (this.state.activeNode !== node) {
      debug('[onClick()] selecting asset: ', node);
      this.setState({ activeNode: node });
      onSelect(node);
    }
    // this.scrollToPosition(node.getFuid());
  };

  renderNode = (node: AssetTreeNode, depth: number) => {
    const TreeViewNode = this.TreeViewNode;
    const { getIcon, getLabel, getIsLeaf } = this.props;
    const { activeNode, expandedNodes } = this.state;
    const id = node.getFuid();
    const showExpanded = !!expandedNodes[id];
    // if (showExpanded && !node.isChildLoaded() && id > 0) {
    //delete expandedNodes[id];
    //showExpanded = false;
    // }

    const props: TreeViewNodeProps = {
      showView: this.props.showTreeView,
      key: id,
      node,
      isActive: !Store.get(ALL_FILES_OPTION) ? (activeNode ? activeNode.getFuid() === node.getFuid() : false) : false,
      isExpanded: showExpanded,
      isLeaf: getIsLeaf(node),
      depth,
      onClick: this.onClick,
      getIcon,
      getLabel,
      onContextMenuClick: this.onContextMenuClick,
      renderChildren: (parent: AssetTreeNode, depth: number) => {
        const children = parent.getChildren().map((node: AssetTreeNode) => this.renderNode(node, depth));
        //$FlowFixMe
        return <div className="children">{children}</div>;
      }
    };

    return <TreeViewNode {...props} />;
  };

  render() {
    const { root } = this.props;
    const { showContextMenu } = this.state;
    let treeHeight =
      document.querySelector('[name=AllFilesTree]') && document.querySelector('.folder')
        ? window.innerHeight -
        (70 +
          30 +
          document.querySelector('[name=AllFilesTree]').offsetHeight +
          document.querySelector('.folder').offsetHeight)
        : null;
    treeHeight = isMobile() ? treeHeight + 20 : treeHeight - 20;

    if (this.props.isIndependentTreeIns) {
      treeHeight = null;
    }

    return (
      <React.Fragment>
        <div
          name="TreeView"
          ref={this.onRef}
          style={treeHeight ? { height: treeHeight + 'px', overflow: 'auto' } : null}>
          {root.isHide() ? root.getChildren().map((n) => this.renderNode(n, 0)) : this.renderNode(root, 0)}
        </div>
        {
            <MorePopover
              ref={(morePopNode) => (this._morePopup = morePopNode)}
              asset={this.activeContext}
              onRefreshPWD={this.props.onRefreshPWD}
              permission={{ canShowSpotLightAction: false }}
              onDeleteSuccess={this._assetListBlockRef.onDeleteSuccess}
              onMenuClose={this.onMenuClose}
              onMenuOpen={this.onMenuOpen}
              onRenameSuccess={this._assetListBlockRef.onRenameSuccess}
              showPopover={showContextMenu}
              closeContextMenu={this.closeContextMenu}
              downloadAsset={this._assetListBlockRef.downloadSingleAsset}
            />
        }
      </React.Fragment>
    );
  }
}

type TreeViewNodeProps = {
  node: AssetTreeNode,
  isActive: boolean,
  isExpanded: boolean,
  isLeaf: boolean,
  depth: number,
  onClick: Function,
  getIcon: Function,
  getLabel: Function,
  renderChildren: Function,
  isOver?: boolean,
  connectDropTarget?: Function,
  asset: RepoItem,
  showView: Boolean
};

function TreeViewNode(props: TreeViewNodeProps) {
  const {
    node,
    isActive,
    isExpanded,
    isLeaf,
    depth,
    onClick,
    getIcon,
    getLabel,
    renderChildren,
    isOver,
    connectDropTarget,
    asset,
    onContextMenuClick,
    showView
  } = props;

  const paddingLeft = depth * 12 + 2;
  const offset = paddingLeft + (isLeaf ? 36 : 54);
  //const maxWidth = 240 - offset;
  const maxWidth = 240;

  const infoStyle = { paddingLeft };
  const icon = isActive
    ? 'images/arrow_active.svg'
    : isExpanded
      ? ThemeSetup.getImageURL('arrow_expanded.svg')
      : ThemeSetup.getImageURL('arrow.svg');

  const handleDragHover = (e, asset) => {
    if (System.isDesktop()) {
      if (_.isEmpty(Store.get(DRAG_ITEMS))) {
        toast(t('UPLOAD_TO') + asset.getName());
      }
    }
    if ('dragover' == e.type) {
      window.gs.dropDir = asset;
      e.currentTarget.style.opacity = 0.5;
      e.currentTarget.classList.add('dropActive');
    } else {
      window.gs.dropDir = null;
      e.currentTarget.style.opacity = 1;
      e.currentTarget.classList.remove('dropActive');
    }
  };

  const onDropHandle = (e, asset) => {
    // if (System.isDesktop()) {
    e.currentTarget.style.opacity = 1;
    e.currentTarget.classList.remove('dropActive');
    // }
  };
  const content = (
    <div
      className={classNames('info', { 'drag-hover': isOver && !isLeaf })}
      onDragOver={(e) => handleDragHover(e, node.getRepoItem())}
      onDragLeave={(e) => handleDragHover(e, node.getRepoItem())}
      onDrop={onDropHandle}
      onContextMenu={(e) => onContextMenuClick(e, node)}
      onClick={(e) => onClick(e, node, false)}
      drop-fuid={node.getRepoItem().getFuid()}
      style={infoStyle}>
      {isLeaf ? null : <ExpandButton isExpanded={isExpanded} onClick={(e) => onClick(e, node, true)} icon={icon} />}
      {node.isLoading() ? <Spinner small /> : <img src={getIcon(node, isExpanded, isActive)} />}
      {/* <AssetStatusTreeView asset={node.getRepoItem()} /> */}
      <label
        className={isExpanded && !isActive ? 'expanded' : ''}
        style={isNonAliveAsset(node.getRepoItem()) ? { opacity: '30%' } : null}>
        <TextWithEllipsisFileExtension
          text={getLabel(node)}
          title={getLabel(node)}
          textStyle={{ fontSize: 14, maxWidth }}
          responsive={false}
        />
      </label>
    </div>
  );

  return (
    <div
      name="TreeViewNode"
      fuid={node.getRepoItem().getFuid()}
      treefuid="true"
      style={showView ? { minWidth: 'fit-content' } : {}}
      className={classNames({ active: isActive, firstNode: node.getRepoItem().getFuid() == 0 })}>
      {connectDropTarget ? connectDropTarget(content) : content}
      {!isLeaf && isExpanded ? renderChildren(node, depth + 1) : null}
    </div>
  );
}
